import { memo, useMemo, useCallback, useState } from 'react';
import {
  TiptapEditorView,
  TiptapOutput,
  Bold,
  Italic,
  Link,
  ClearFormatting,
  BulletList,
  Code,
  OrderedList,
  Image,
} from '@amzn/awscat-editor';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { isURL } from 'class-validator';
import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-core';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { fetchImageInBase64Format } from '../../editor/utils';
import { useEntities } from '../v2-hooks/useEntities';
import styles from './PatternCellField.module.scss';
import {
  TiptapEditorEditingMode,
  TiptapEditorViewingMode,
} from '../v2-common/tiptap-editor-integration';
import { InlineTextFilter } from '../../services/InlineTextOperations';
import { SaveDraftError, ServiceCallError } from '../../services/Errors';

/** Only JSON — no legacy HTML needed for structured cells. */
const EDITOR_OUTPUT_FORMATS = ['json'] as unknown as 'json'[];

export type PatternCellFieldProps = {
  /** Initial Tiptap content. Memoized internally to prevent TiptapEditor re-mount. */
  readonly initialValue: TiptapOutputType;
  /** Called on every edit with the updated Tiptap output. */
  readonly onChange?: (output: TiptapOutput) => void;
  /** When true, renders a read-only view. When false, renders the editable toolbar + editor. */
  readonly isReadonly: boolean;
  /** Which content region's inline edits and comments to display, in both edit and read-only modes. */
  readonly inlineTextFilter?: InlineTextFilter;
  /** When true, focuses the editor on mount. */
  readonly isAutoFocused?: boolean;
  /** Content ID for image upload. When provided, enables Image and YouTube toolbar items. */
  readonly contentId?: string;
};

export const PatternCellField = memo(function PatternCellField({
  initialValue,
  onChange,
  isReadonly,
  isAutoFocused = false,
  contentId,
  inlineTextFilter,
}: PatternCellFieldProps) {
  const mplService = useMplServiceClient();
  const editorService = useContentEditorService();
  const entities = useEntities();
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  const uploadImage = useCallback(
    async (image: File): Promise<string> => {
      if (!contentId) return '';

      // Take the lock first because the server accepts uploads only from the lock holder.
      // Show failures here and rethrow, since the editor package only logs them.
      try {
        await editorService.acquireLockIfNeeded();
      } catch (error) {
        setValidationError(
          error instanceof SaveDraftError
            ? error.message
            : 'Failed to take the edit lock. Please try again.',
        );
        throw error;
      }

      let response;
      try {
        response = await mplService.putContentImage(contentId, image);
      } catch (error) {
        setValidationError(
          error instanceof ServiceCallError
            ? `Failed to upload the image: ${error.message}`
            : 'Failed to upload the image. Please try again.',
        );
        throw error;
      }
      return response.data.link;
    },
    [editorService, mplService, contentId],
  );

  const fetchImage = useCallback(
    async (srcUrl: string): Promise<string> => fetchImageInBase64Format(srcUrl, mplService),
    [mplService],
  );

  const validateLink = useCallback(async (link: string): Promise<boolean> => {
    if (link.startsWith('/') || link.startsWith('#')) return true;
    if (!isURL(link) || !link.toLowerCase().startsWith('https://')) {
      setValidationError('Could not insert link. Your URL is invalid or not using HTTPS.');
      return false;
    }
    return true;
  }, []);

  const [menuItems] = useState(() => {
    const items = [
      new Bold(),
      new Italic(),
      new Code(),
      new OrderedList(),
      new BulletList(),
      new Link({
        beforeInsert: validateLink,
        validateLink: (link) => /(^https:\/\/)|(^www\.)/.test(link),
      }),
    ];
    if (contentId) {
      items.push(new Image({ imageUpload: uploadImage }) as any);
    }
    items.push(new ClearFormatting());
    return items;
  });

  // Re-capture initialValue each time the field enters edit mode so
  // the editor sees the latest persisted value, not a stale first-mount snapshot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const content = useMemo(() => initialValue, [isReadonly]);

  const handleChange = useCallback(
    (_editor: unknown, output: TiptapOutput) => {
      onChange?.(output);
    },
    [onChange],
  );

  const clearValidationError = useCallback(() => setValidationError(undefined), []);

  if (isReadonly) {
    // A filter enables comment highlighting; without one there are no comments to show,
    // so fall back to the plain view to avoid the comment subsystem's overhead.
    if (inlineTextFilter) {
      return (
        <TiptapEditorViewingMode
          model={initialValue}
          entities={entities}
          fetchImage={fetchImage}
          inlineTextFilter={inlineTextFilter}
        />
      );
    }
    return <TiptapEditorView model={initialValue} entities={entities} fetchImage={fetchImage} />;
  }

  return (
    <div className={styles.editorContainer}>
      <TiptapEditorEditingMode
        content={content}
        menuItems={menuItems}
        onChange={handleChange}
        outputFormats={EDITOR_OUTPUT_FORMATS}
        entities={entities}
        imageUpload={contentId ? uploadImage : undefined}
        fetchImage={fetchImage}
        editable
        useContainer={false}
        hideFooter={true}
        autoFocus={isAutoFocused}
        inlineTextFilter={inlineTextFilter}
      />
      {validationError && (
        <Modal
          visible
          onDismiss={clearValidationError}
          header="Validation error"
          closeAriaLabel="Close"
          footer={
            <Box float="right">
              <Button variant="primary" onClick={clearValidationError}>
                OK
              </Button>
            </Box>
          }
        >
          <SpaceBetween size="s">{validationError}</SpaceBetween>
        </Modal>
      )}
    </div>
  );
});
